<template>
  <div class="form__sidebar group-sidebar">
    <div class="sidebar-block pb-3">
      <v-text-field
        class="dark-input mb-4"
        label="Name"
        v-model="group.name"
        persistent-hint
        :disabled="nameFieldDisabled"
        hint="Only you and your team will see this name"
        :rules="[(v) => validationRules.checkRequired(v, 'Group Name')]"
      />

      <div class="d-flex align-center" v-if="!triggerOnly">
        <v-select
          v-if="!group.team"
          label="Group Folder"
          v-model="group.folder_id"
          :items="foldersOptions"
          :disabled="disableEditing"
        />

        <v-tooltip right v-if="group.folder_id">
          <template v-slot:activator="{ on }">
            <v-btn
              fab
              icon
              small
              v-on="on"
              :href="'/groups/folders/' + group.folder_id"
              target="_blank"
            >
              <v-icon color="appMain">info</v-icon>
            </v-btn>
          </template>
          <span>Open Folder</span>
        </v-tooltip>
      </div>

      <div class="mt-4">
        <v-select
          v-model="filterType"
          :items="matchOptions"
          :disabled="disableEditing || isTagGroup"
          label="match"
          @input="prepareFiltersToSend"
        />
      </div>

      <div class="mt-2">
        <input
          @paste="onPaste"
          v-model="pastedFilters"
          class="mt-4 group-filter-paste-input"
          placeholder="Paste Filters"
        />
      </div>
    </div>
    <!-- end of sidebar -->

    <v-expansion-panels accordion v-model="panel" multiple>
      <v-expansion-panel
        expand
        v-for="(filter, idx) in parsedFilters"
        :key="getFilterKey(filter, idx)"
      >
        <v-expansion-panel-header>
          <content-line
            :icon="allowFilterSelection ? null : 'custom/filter'"
            :text="getFilterName(filter)"
            :selectedText="getFilterText(filter, idx)"
          >
            <template v-if="allowFilterSelection" #customIcon>
              <div
                class="group-filter-select-icon"
                :class="{
                  'group-filter-select-icon--selected': isFilterSelected(
                    filter,
                    idx
                  ),
                }"
                @click.stop="toggleFilterSelection(filter, idx)"
              >
                <v-checkbox
                  class="group-filter-select-icon__checkbox"
                  :input-value="isFilterSelected(filter, idx)"
                  hide-details
                  dense
                  color="primary"
                  @click.stop="toggleFilterSelection(filter, idx)"
                />
                <icon-base
                  class="group-filter-select-icon__filter-icon"
                  icon="custom/filter"
                />
              </div>
            </template>
          </content-line>
        </v-expansion-panel-header>
        <v-expansion-panel-content eager>
          <template v-if="Array.isArray(filter)">
            <group-filter-block
              v-for="(filterItem, filtIdx) in filter"
              :key="ensureFilterUid(filterItem)"
              :activity="filter[0].value"
              class="mt-3"
              :filter="filterItem"
              :filtersGroup="filter"
              :disableEditing="disableEditing"
              @showModal="showModal($event, filterItem, idx)"
              @delete="removeFilterRow(idx, filtIdx)"
            />

            <v-btn
              v-if="showAddFieldButton(filter) && !disableEditing"
              color="primary"
              block
              class="light--text mt-3"
              @click="addFilterRow(idx)"
            >
              Add Field
            </v-btn>
          </template>

          <group-filter-block
            v-else
            class="mt-3"
            :filter="filter"
            :tagGroup="isTagGroup"
            :disableEditing="disableEditing"
            @showModal="showModal($event, filter, idx)"
          />

          <v-row
            v-if="!isTagGroup && !disableEditing"
            class="group-filter__actions"
            no-gutters
          >
            <v-col class="pr-2">
              <v-btn color="primary" block @click="copyFilterRow(idx)">
                Copy
              </v-btn>
            </v-col>
            <v-col class="pl-2">
              <v-btn color="appMain" @click="removeFilterRow(idx)" block>
                Remove
              </v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <content-line
        v-if="!isTagGroup && !disableEditing"
        class="add-rule"
        icon="custom/add"
        text="Add filter"
        @click.native="addFilterRow()"
      />
    </v-expansion-panels>

    <v-dialog
      :value="!!filterModal"
      max-width="480px"
      content-class="fixed-height-md"
      @input="filterModal = null"
    >
      <component
        v-if="filterModal"
        :is="filterModal.type"
        :filter="filterModal.filter"
        :filterEvent="getFilterEvent(currentModalFilter)"
        :filtersGroup="parsedFilters[filterModal.idx]"
        :meta="meta"
        :teamFilterOff="teamFilterOff"
        :contactFieldsOnly="contactFieldsOnly"
        @updateFieldValue="handleLessonChange($event, filterModal.idx)"
        @select="selectField"
        @cancel="onModalCancel"
        @close="filterModal = null"
      />
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';

import {
  activities,
  FIXED_FIELDS,
  eventsWithOptionalFields,
  hasCopyableFilters,
  sectionDefinition,
  getSection,
} from './groupHelpers';

import filterWizardMixin from './filterWizardMixin';
import validationRules from '@/lib/validationRules';

import GroupFilterBlock from './GroupFilterBlock';
import GroupFieldSelect from './GroupFieldSelect';
import GroupFieldSelectValue from './GroupFieldSelectValue';

let filterUidCounter = 0;

const createFilterUid = () => `group-filter-${++filterUidCounter}`;

export default {
  name: 'GroupFormSidebar',
  mixins: [filterWizardMixin],
  components: {
    GroupFilterBlock,
    GroupFieldSelect,
    GroupFieldSelectValue,
  },
  props: {
    group: {
      type: Object,
      default: () => ({}),
    },
    disableEditing: Boolean,
    disableNameEditing: Boolean,
    triggerOnly: Boolean,
    teamFilterOff: Boolean,
    meta: {
      type: Object,
      default: () => ({}),
    },
    contactFieldsOnly: Boolean,
  },
  data() {
    return {
      filterModal: null,
      panel: [],
      filterType: '$and',
      parsedFilters: [],
      selectedFilterKeys: [],
      eventType: '',
      eventTypes: activities,
      showError: false,
      getSection,
      validationRules,
      matchOptions: [
        {
          text: '(AND) Match All Filters',
          value: '$and',
        },
        {
          text: '(OR) Match Any Filter',
          value: '$or',
        },
      ],
      pastedFilters: '',
    };
  },
  watch: {
    loaded(newVal) {
      if (newVal && !this.parsedFilters.length) {
        this.initFilters();
      }
    },
    parsedFilters: {
      deep: true,
      handler() {
        this.updateTagGroup();
        this.prepareFiltersToSend();
        this.emitCopyableFiltersChange();
      },
    },
    filterType() {
      this.prepareFiltersToSend();
      this.emitCopyableFiltersChange();
    },
  },

  computed: {
    ...mapGetters('fields', ['loaded', 'preparedFields', 'additionalFields']),
    ...mapGetters('groupsFolders', {
      folders: 'all',
    }),
    nameFieldDisabled() {
      return this.disableEditing || this.group?.team || this.disableNameEditing;
    },
    filters: {
      get() {
        return this.group.filters;
      },
      set(val) {
        this.group.filters = isEmpty(val) ? { $and: [] } : val;
      },
    },
    foldersOptions() {
      return (this.folders || []).reduce(
        (acc, i) => {
          acc.push({ text: i.name, value: i.id });
          return acc;
        },
        [{ text: 'None', value: '' }]
      );
    },
    additionalFilters() {
      return this.parsedFilters.filter((i) => Array.isArray(i));
    },
    attributesFilters() {
      return this.parsedFilters.filter(
        (i) => i.field && i.field.group !== 'rep'
      );
    },
    currentModalFilter() {
      if (!this.filterModal) return null;
      return this.parsedFilters[this.filterModal.idx];
    },
    isTagGroup() {
      return this.group.type === 'tag';
    },
    allowFilterSelection() {
      return !this.disableEditing && !this.isTagGroup;
    },
  },

  methods: {
    nextFilterUid() {
      return createFilterUid();
    },

    ensureFilterUid(filter) {
      if (!filter._uid) {
        filter._uid = this.nextFilterUid();
      }
      return filter._uid;
    },

    assignFreshUids(filterOrGroup) {
      if (Array.isArray(filterOrGroup)) {
        filterOrGroup.forEach((filter) => this.assignFreshUids(filter));
        return;
      }

      if (filterOrGroup && typeof filterOrGroup === 'object') {
        filterOrGroup._uid = this.nextFilterUid();
      }
    },

    getFilterKey(filter, idx) {
      if (Array.isArray(filter)) {
        return filter.map((item) => this.ensureFilterUid(item)).join(':');
      }

      return this.ensureFilterUid(filter) || idx;
    },

    isFilterSelected(filter, idx) {
      return this.selectedFilterKeys.includes(this.getFilterKey(filter, idx));
    },

    toggleFilterSelection(filter, idx) {
      const key = this.getFilterKey(filter, idx);

      if (this.isFilterSelected(filter, idx)) {
        this.selectedFilterKeys = this.selectedFilterKeys.filter(
          (selectedKey) => selectedKey !== key
        );
        return;
      }

      this.selectedFilterKeys = [...this.selectedFilterKeys, key];
    },

    syncFilterSelection() {
      const validKeys = this.parsedFilters.map((filter, idx) =>
        this.getFilterKey(filter, idx)
      );

      this.selectedFilterKeys = this.selectedFilterKeys.filter((key) =>
        validKeys.includes(key)
      );
    },

    getFiltersListForCopy() {
      if (!this.selectedFilterKeys.length) {
        return this.parsedFilters;
      }

      return this.parsedFilters.filter((filter, idx) =>
        this.selectedFilterKeys.includes(this.getFilterKey(filter, idx))
      );
    },

    buildFiltersPayload(parsedFiltersList) {
      const attributes = parsedFiltersList.filter(
        (filter) =>
          !Array.isArray(filter) && filter.field && filter.field.group !== 'rep'
      );
      const additional = parsedFiltersList.filter((filter) =>
        Array.isArray(filter)
      );

      let filters = {};

      if (attributes.length) {
        filters[this.filterType] = this.cleanFiltersArr(attributes);
      }

      if (additional.length) {
        const records = additional.map((filtersRow) =>
          this.cleanFiltersArr(filtersRow)
        );
        const currRecords = this.filters['$records'];
        if (
          this.filterType === '$or' ||
          (currRecords && (currRecords['$or'] || currRecords['$and']))
        ) {
          filters['$records'] = { [this.filterType]: records };
        } else {
          filters['$records'] = records;
        }
      }

      return filters;
    },

    getFiltersForClipboard() {
      this.prepareFiltersToSend();

      const filtersList = this.getFiltersListForCopy();
      if (filtersList.length) {
        const payload = this.buildFiltersPayload(filtersList);
        if (hasCopyableFilters(payload)) {
          return payload;
        }
      }

      return this.group.filters || {};
    },

    hasCopyableFiltersState() {
      const filtersList = this.getFiltersListForCopy();
      if (filtersList.length) {
        return hasCopyableFilters(this.buildFiltersPayload(filtersList));
      }

      return hasCopyableFilters(this.group?.filters);
    },

    emitCopyableFiltersChange() {
      this.$emit('copyable-filters-change', this.hasCopyableFiltersState());
    },

    hasFiltersToParse() {
      const filters = this.filters || {};

      if (Array.isArray(filters.$and) && filters.$and.length) {
        return true;
      }

      if (Array.isArray(filters.$or) && filters.$or.length) {
        return true;
      }

      return !!filters.$records;
    },

    createFilterObject() {
      return {
        _uid: this.nextFilterUid(),
        field: '',
        operator: '',
        value: '',
      };
    },

    showAddFieldButton(filter) {
      const event = this.getFilterEvent(filter);
      return (
        eventsWithOptionalFields.includes(event) || !sectionDefinition[event]
      );
    },
    getFilterEvent(filter = []) {
      if (!filter || !filter.length) return '';
      let event = filter.find((i) => i.field.value === 'event');
      return event ? event.value : '';
    },
    getFilterName(filter) {
      let filterName = 'Filter by ';
      if (filter.field) {
        filterName += filter.field.text;
      }
      if (Array.isArray(filter)) {
        filterName += 'event';
      }

      return filterName;
    },
    getFilterText(filter, idx) {
      if (this.panel.length && this.panel.some((i) => i === idx)) {
        return false;
      }

      if (Array.isArray(filter)) {
        const lessonsEvents = ['completed_lesson', 'started_lesson'];
        const event = this.getFilterEvent(filter);
        const eventType = lessonsEvents.includes(event) ? 'lesson' : 'campaign';
        const selectedItem = filter.find((i) => i.field.field === eventType);

        let selectedValue = selectedItem?.value || '';
        if (Array.isArray(selectedValue)) {
          selectedValue = selectedValue[0];
        }

        return event + ` - ${selectedValue?.text || selectedValue}`;
      }

      if (
        (!filter.value && !Array.isArray(filter)) ||
        filter.operator.key === '$exists'
      ) {
        return filter.operator.label;
      }

      let text = (filter.operator?.label || filter.operator) + ' - ';
      if (Array.isArray(filter.value)) {
        filter.value.forEach((i, idx) => {
          text += (idx > 0 ? ', ' : ' ') + (i?.text || i);
        });
      } else {
        text += filter.value.text ? filter.value.text : filter.value;
      }

      return text;
    },
    showModal(type, filter, idx) {
      this.filterModal = {
        type: `Group${type}`,
        filter,
        idx,
      };
    },
    selectField(field) {
      let filterIdx = this.filterModal.idx;
      let currentFilter = this.parsedFilters[this.filterModal.idx];
      let currentField = this.filterModal.filter.field;

      if (field.activity && this.contactFieldsOnly) {
        return;
      }

      // if field is activity add new section
      if (field.activity) {
        this.addActivity(field.value, this.filterModal.idx);
        return;
      }

      // if activity filter
      if (Array.isArray(currentFilter)) {
        if (currentField.field === 'event') {
          this.$set(
            this.parsedFilters,
            this.filterModal.idx,
            this.filterModal.filter
          );
          setTimeout(() => {
            this.parsedFilters[filterIdx].field = field;
          });
        } else {
          this.filterModal.filter.field = field;
        }
      } else {
        this.parsedFilters[filterIdx].field = field;
      }

      this.filterModal = null;
    },
    initFilters() {
      this.selectedFilterKeys = [];
      this.parsedFilters = [];
      this.eventType = this.getEventType();
      let filters = this.filters || {};

      //set filter type
      this.filterType = filters['$or'] ? '$or' : '$and';

      // if filters obj is empty - create default filter
      if (
        filters['$and'] &&
        !filters['$and'].length &&
        !filters['$or'] &&
        !filters['$records']
      ) {
        //this.parsedFilters.push(clone(defaultFilterObject))
        return;
      }

      // parse filters
      for (let key in filters) {
        let filtersArr = [];
        if (key === '$and' || key === '$or') {
          filtersArr = this.prepareField(filters[key]);
        }
        if (key === '$records') {
          let groupFilters = filters[key];
          if (groupFilters[this.filterType]) {
            groupFilters = groupFilters[this.filterType];
          }

          // there are no points_scored event on the backend,
          // but we need it, to show activity of this type
          if (
            groupFilters[0].some((i) => i.score) &&
            !groupFilters[0].some((i) => i.event)
          ) {
            groupFilters[0].unshift({ event: { $eq: 'points_scored' } });
          }

          filtersArr = groupFilters.map((i) => this.prepareField(i, true));
        }

        this.parsedFilters.push(...filtersArr);
      }
    },

    // Set Event Type for Activity Field
    getEventType() {
      const firstSection = this.additionalFilters[0];
      if (!firstSection || !firstSection.length) return '';

      const eventField = firstSection[0];
      if (eventField.field.field === 'event') {
        if (eventField.value === 'answered_question') return 'answeredQuestion';
        if (eventField.value === 'read_content') return 'readContent';
        return 'event';
      }
      return 'score';
    },

    getFieldKey(field) {
      if (!field) return '';
      const fieldName = field.split('.');
      return fieldName[1] || fieldName[0];
    },

    isFixedField(fieldKey) {
      return FIXED_FIELDS.some((i) => i.field === fieldKey);
    },

    getFieldSchema(fieldKey, checkFixed = false) {
      let field = null;
      if (checkFixed && this.isFixedField(fieldKey)) {
        field = FIXED_FIELDS.find((i) => i.field === fieldKey);
      } else {
        field = this.preparedFields.find((i) => i.field === fieldKey);
      }

      if (!field) return null;

      return {
        ...field,
        fixed: checkFixed,
      };
    },

    // Convert filters from server to js friendly format
    prepareField(filtersArr, checkFixed = false) {
      return filtersArr.map((filter) => {
        const field = Object.keys(filter)[0];
        const operator = Object.keys(filter[field])[0];
        const fieldKey = this.getFieldKey(field);
        const fieldValue = filter[field][operator];

        return {
          _uid: this.nextFilterUid(),
          field: this.getFieldSchema(fieldKey, checkFixed),
          operator,
          value: fieldValue,
        };
      });
    },

    prepareFiltersToSend() {
      let filters = this.buildFiltersPayload(this.parsedFilters);

      if (
        !this.attributesFilters.length &&
        this.filters &&
        (this.filters['$and'] || this.filters['$or']) &&
        !filters[this.filterType]
      ) {
        filters[this.filterType] = this.cleanFiltersArr(this.attributesFilters);
      }

      this.filters = filters;
    },

    updateTagGroup() {
      if (!this.isTagGroup) return;

      const filterField = (this.attributesFilters || []).find(({ field }) => {
        return field?.field === 'tags';
      });
      const value = filterField?.value;
      // List operators make the value an array; the group tag is a single string.
      const tag = Array.isArray(value) ? value[0] : value;
      if (tag && tag !== this.group.tag) {
        this.group.tag = tag;
      }
    },

    cleanFiltersArr(arr) {
      return arr
        .filter((i) => i.field && i.operator && i.value !== 'points_scored')
        .map((filter) => {
          let filterValue = filter.value;

          if (Array.isArray(filter.value)) {
            filterValue = filter.value.map((i) => (i?.value ? i.value : i));
          }

          if (filterValue?.value) {
            filterValue = filterValue.value;
          }

          const operator =
            filter.operator?.operator ||
            filter.operator?.value ||
            filter.operator;

          return {
            [filter.field.value]: {
              [operator]: filterValue,
            },
          };
        });
    },

    copyFilterRow(idx) {
      const filterCopy = cloneDeep(this.parsedFilters[idx]);
      this.assignFreshUids(filterCopy);
      this.parsedFilters.splice(idx + 1, 0, filterCopy);
    },

    addFilterRow(idx, filter) {
      let newFilter = filter || this.createFilterObject();
      if (idx >= 0) {
        this.parsedFilters[idx].push(newFilter);
      } else {
        this.parsedFilters.push(newFilter);
        this.panel.push(this.parsedFilters.length - 1);
      }
    },

    removeFilterRow(filterIndex, itemIndex, forceRemove = false) {
      if (itemIndex !== undefined) {
        this.parsedFilters[filterIndex].splice(itemIndex, 1);
        this.syncFilterSelection();
        return;
      }

      this.removeFilterConfirmation(() => {
        const key = this.getFilterKey(
          this.parsedFilters[filterIndex],
          filterIndex
        );
        this.selectedFilterKeys = this.selectedFilterKeys.filter(
          (selectedKey) => selectedKey !== key
        );
        this.parsedFilters.splice(filterIndex, 1);
      }, forceRemove);
    },

    removeFilterConfirmation(onConfirm, forceRemove) {
      if (forceRemove) return onConfirm();
      this.openConfirmationModal({
        title: 'Remove Filter',
        text: 'Are you sure you want to remove this filter?',
        onConfirm,
      });
    },

    addActivity(value, idx) {
      if (!value) return false;

      let sectionFields = this.getSection(value);
      let newSection = this.prepareField(sectionFields, true);
      this.parsedFilters[idx] = [];
      setTimeout(() => {
        this.$set(this.parsedFilters, idx, newSection);
        this.checkWizard(value, idx);
        this.syncFilterSelection();
      });
    },

    // clear lesson item | remove lesson items fields - answer/points etc.
    handleLessonChange(fieldName, idx) {
      const lessonFields = ['lesson', 'item'];
      if (!lessonFields.includes(fieldName)) return;

      let filtersGroup = this.parsedFilters[idx];
      const lessonItemFields = ['answer', 'points'];

      if (fieldName === 'lesson') {
        const itemIdx = filtersGroup.findIndex(
          ({ field }) => field.field === 'item'
        );
        if (itemIdx > -1) {
          filtersGroup[itemIdx].value = '';
        }
      }

      lessonItemFields.forEach((key) => {
        let itemIdx = filtersGroup.findIndex(
          ({ field }) => field.field === key
        );
        if (itemIdx > -1) {
          filtersGroup.splice(itemIdx, 1);
        }
      });

      this.findNextFieldForEvent();
    },

    async openPanelsWithErrors() {
      await this.$nextTick();
      document
        .querySelectorAll('.group-sidebar .v-expansion-panel')
        .forEach((i, idx) => {
          if (!i.querySelector('.error--text')) return;
          if (!this.panel.includes(idx)) {
            this.panel.push(idx);
          }
        });
    },

    onModalCancel() {
      this.filterModal = null;
      this.cancelWizard();
    },

    getPastedFilters(e) {
      const text = e.clipboardData.getData('text');
      try {
        return JSON.parse(text);
      } catch (error) {
        return null;
      }
    },

    onPaste(e) {
      const filters = this.getPastedFilters(e);

      setTimeout(() => {
        this.pastedFilters = '';
      }, 100);

      if (!filters || (!filters.$or && !filters.$and && !filters.$records)) {
        return false;
      }

      this.filters = filters;
      this.initFilters();
    },
  },
  mounted() {
    if (this.loaded || !this.hasFiltersToParse()) {
      this.initFilters();
    }

    this.$nextTick(() => {
      this.emitCopyableFiltersChange();
    });
  },
};
</script>

<style lang="less">
.add-rule {
  width: 100%;
  border-top: 2px solid var(--v-borderColor-base);
  cursor: pointer;
}

.additional-filters {
  width: 100%;
  padding: 8px 16px 16px;
}

.group-section + .group-section {
  border-top: 2px solid var(--v-borderColor-base);
  margin-top: 16px;
  padding-top: 4px;
}

.group-section__button-add {
  border-top: 2px solid var(--v-borderColor-base);
  padding-top: 16px;
}

.group-filter__actions {
  border-top: 2px solid var(--v-borderColor-base);
  margin-top: 8px !important;
  padding-top: 12px;
}

.group-filter-paste-input {
  line-height: 24px;
  width: 100%;
  opacity: 0.5;
  &:focus {
    outline: none;
  }
}

.group-filter-select-icon {
  height: 36px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &__checkbox {
    display: none;
    margin: 0;
    padding: 0;

    .v-input--selection-controls__input {
      margin-right: 0;
    }
  }

  &__filter-icon {
    display: flex;
  }

  &:hover &__checkbox,
  &--selected &__checkbox {
    display: flex;
  }

  &:hover &__filter-icon,
  &--selected &__filter-icon {
    display: none;
  }
}
</style>
